import { useGetRiskFactorsV1RiskFactorsGet } from '@/api/generated-catch-backend/risk-factors/risk-factors';
import { ROUTE_RISK_ANALYSIS } from '@/constants/routes';
import { PortableText } from '@portabletext/react';
import { useNavigate } from 'react-router-dom';
import { RiskFactorCard } from './components/risk-factor-card';
import useAnalyticsCalls, { DashboardSource } from '@/hooks/use-analytics';

export default function RiskFactors() {
  const { data: riskFactorsData, isLoading, error } = useGetRiskFactorsV1RiskFactorsGet();
  const navigate = useNavigate();
  const { setNavigationSource } = useAnalyticsCalls();

  if (isLoading) {
    return (
      <div className="risk-cards-container">
        <div className="w-full max-w-[650px]">
          <div className="h-7 w-48 animate-pulse rounded-full bg-gray-300" />
          <div className="mt-2 h-5 w-full animate-pulse rounded-full bg-gray-200" />
        </div>

        <div className="flex w-full max-w-[295px] gap-3 overflow-x-auto md:max-w-full">
          {[1, 2, 3].map((index) => (
            <div
              key={`skeleton-${index}`}
              className="relative h-[183px] w-[208px] flex-shrink-0 rounded-[10px] border border-[#ededed] bg-white p-3"
            >
              <div className="flex h-full flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="h-[21px] w-24 animate-pulse rounded bg-gray-200" />
                  <div className="h-[39px] w-[39px] animate-pulse rounded-full bg-gray-200" />
                </div>
                <div className="mt-4 space-y-2">
                  <div className="h-5 w-28 animate-pulse rounded bg-gray-200" />
                  <div className="space-y-1">
                    <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
                    <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="cta-button-container">
          <div className="h-10 w-[293px] animate-pulse rounded-full bg-gray-300" />
        </div>
      </div>
    );
  }

  if (error || !riskFactorsData) {
    return (
      <div className="risk-cards-container">
        <div className="w-full max-w-[650px]">
          <h2 className="mobile-h3-primary text-primary text-left font-bold">Your Top Risk Factors</h2>
          <p className="body-regular text-primary mt-2 text-left">Unable to load risk factors at this time.</p>
        </div>
      </div>
    );
  }

  const { topFactors, riskFactors, summaryText } = riskFactorsData;
  const topRiskFactors = [
    {
      ...topFactors.topDecreasing,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...riskFactors.find((factor) => factor.slug === topFactors.topDecreasing.slug)!,
      type: 'decreasing' as const,
    },
    {
      ...topFactors.topIncreasing,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...riskFactors.find((factor) => factor.slug === topFactors.topIncreasing.slug)!,
      type: 'increasing' as const,
    },
    {
      ...topFactors.topModifiable,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...riskFactors.find((factor) => factor.slug === topFactors.topModifiable.slug)!,
      type: 'modifiable' as const,
    },
  ];

  return (
    <div className="risk-cards-container">
      <div className="w-full max-w-[650px]">
        <h2 className="mobile-h3-primary text-primary text-left font-bold">Your Top Risk Factors</h2>
        <PortableText
          value={[...summaryText]}
          components={{
            block: {
              normal: ({ children }) => <p className="body-regular text-primary text-left pt-2">{children}</p>,
            },
            marks: {
              strong: ({ children }) => <strong className="text-bold">{children}</strong>,
            },
          }}
        />
      </div>

      <div className="flex w-full max-w-[295px] gap-3 overflow-x-auto md:max-w-full items-stretch">
        {topRiskFactors.map((factor, index) => (
          <RiskFactorCard key={`${factor.type}-${index}`} riskFactor={factor} type={factor.type} text={factor.text} />
        ))}
      </div>

      <div className="cta-button-container">
        <button
          onClick={() => {
            setNavigationSource(DashboardSource.DASHBOARD_RISK_FACTOR_CTA);
            navigate(ROUTE_RISK_ANALYSIS);
          }}
          className="cta-button body-meta"
        >
          See all Risk Factors
        </button>
      </div>
    </div>
  );
}
